import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import _ from 'underscore';
import './PreviewCourseModule.css'
import QuestionDetails from "./QuestionDetails";
import MdPreview from "./MdPreview";
import MDEditor from "@uiw/react-md-editor";
import Button from "../../libs/Button";
import QuizTraining from "./QuizTraining";
import RunQuiz from "./RunQuiz";
import MyModal from "../../libs/MyModal";
import {Link, useNavigate} from "react-router-dom";
import {generateSuggestion} from "./SuggestionItem";
import CustomStorage from "./CustomStorage";
import Train from "../TrainMethods/Train";
import {getDefaultQuizTime, getStartTimers} from "../RunExam";
import TrainPageCourse from "../TrainMethods/TrainPageCourse";
import quiz from "../Quiz";
import MockInterviewIframe from "../MockInterview/components/MockInterviewIframe";
import CourseInterviewHistory from "../MockInterview/components/CourseInterviewHistory";
import {calcQuizPerc, countVsQuizes} from "../VsTask/vsTask";
import {startInterviewAttempt} from "../MockInterview/startInterviewAttempt";

let quizIteration = 0;

// Статусы попытки, которую человек начал, но не досдал - такую продолжаем.
const UNFINISHED_INTERVIEW_STATUSES = ['draft', 'active', 'started'];

function CourseQuiz(props, ref) {
    let {onAction, isLastModule, title, onSuccess, questionId, moduleId, interviewId, moduleResult} = props;
    // Итог уже сданного модуля из истории курса: без него открытый заново пройденный курс не показывает, сдан ли финальный тест
    let isModuleDone = !questionId && (moduleResult || {}).status === 'ok';
    let navigate = useNavigate();

    let [loading, setLoading] = useState(false);
    let [loadError, setLoadError] = useState(false);
    let [open, setOpen] = useState(false);
    let [confirmClose, setConfirmClose] = useState(false);
    let [opts, setOpts] = useState({});
    let [quizResults, setQuizResults] = useState(false);
    let [quizes, setQuizes] = useState([]);
    let [pubQuizes, setPubQuizes] = useState([]);
    let [quizPerc, setQuizPerc] = useState(0);

    // Для последнего модуля курса с настроенным interviewId кнопка "Проверить
    // знания" больше не открывает модалку с табами: интервью и есть проверка,
    // поэтому по нажатию сразу заводим попытку и открываем iframe записи. Ни
    // карточки старта, ни истории попыток по дороге не показываем - история
    // живёт отдельным блоком ниже на самой странице (CourseInterviewHistory).
    // Если попытку не удалось создать или забронировать (бот занят, ошибка
    // брони) - это не тупик: открываем обычную модалку с квизом, как раньше.
    let hasInterview = isLastModule && !!interviewId;
    let [interviewActive, setInterviewActive] = useState(null);
    let [launchingInterview, setLaunchingInterview] = useState(false);
    let [historyReloadKey, setHistoryReloadKey] = useState(0);
    let interviewAttemptRef = useRef(null);
    let interviewReservedRef = useRef(false);

    // Бронь бота надо отпускать всегда, когда мы ушли с интервью (закрыли iframe,
    // завершили попытку, закрыли вкладку) - иначе бот останется "занят" для всех.
    function releaseInterviewReservation() {
        if (!interviewReservedRef.current || !interviewAttemptRef.current) {
            return;
        }
        interviewReservedRef.current = false;
        global.http.post(`/mock-interview/my-list/${interviewAttemptRef.current._id}/release`, {}, {wo_notify: true})
            .catch(() => {});
    }

    useEffect(() => releaseInterviewReservation, []);

    // Незавершённая попытка (draft/active/started) - это то же самое интервью,
    // которое человек уже начал и не досдал: его надо продолжить, а не плодить
    // на каждое нажатие новую попытку. Ищем самую свежую такую в списке по
    // этому interviewId; нет ни одной - только тогда заводим новую.
    function resolveInterviewAttempt() {
        return global.http.get('/mock-interview/my-list', {filter: {interviewId}}, {wo_notify: true})
            .catch(() => ({}))
            .then(r => {
                let unfinished = (r?.items || [])
                    .filter(attempt => UNFINISHED_INTERVIEW_STATUSES.includes(attempt.status))
                    .sort((a, b) => new Date(b.cd) - new Date(a.cd))[0];
                if (unfinished) {
                    return unfinished;
                }
                return global.http.post('/mock-interview/my-list', {interviewId}, {wo_notify: true})
                    .then(({item: attempt}) => attempt);
            });
    }

    function launchInterview(scb) {
        setLaunchingInterview(true);
        return resolveInterviewAttempt()
            .then((attempt) => {
                interviewAttemptRef.current = attempt;
                return startInterviewAttempt(attempt, {
                    onReserve: () => { interviewReservedRef.current = true; },
                    onRelease: releaseInterviewReservation,
                });
            })
            .then(active => {
                setInterviewActive(active);
                setLaunchingInterview(false);
                scb && scb();
            })
            .catch(err => {
                setLaunchingInterview(false);
                window.notify.warning(err?.message || 'Не удалось начать интервью. Попробуйте ещё раз.');
                // Запасной сценарий - обычный квиз в модалке; если квизов у
                // модуля нет вовсе, показывать в модалке нечего, оставляем
                // человека на странице с предупреждением.
                if (pubQuizes.length) {
                    reGenerateQuiz(scb);
                } else {
                    scb && scb();
                }
            });
    }

    function closeInterview() {
        releaseInterviewReservation();
        setInterviewActive(null);
        setHistoryReloadKey(key => key + 1);
    }

    function completeInterview() {
        let attempt = interviewAttemptRef.current;
        releaseInterviewReservation();
        setInterviewActive(null);
        setHistoryReloadKey(key => key + 1);
        global.http.put(`/mock-interview/my-list/${attempt._id}`, {status: 'completed'}, {wo_notify: true})
            .catch(() => {});
        // Интервью - альтернатива итоговому квизу, а не довесок к нему:
        // пройденное интервью закрывает модуль ровно так же, как сданный квиз -
        // тем же /save-course-module-results со status "ok" и тем же onSuccess,
        // который обновляет mHistory. Без этого модуль оставался незакрытым и
        // курс, пройденный через интервью, показывал не 100%.
        saveResults(100);
        onSuccess && onSuccess({status: 'ok'});
        // Разбор ответа и список вопросов на странице курса не помещаются -
        // уводим на ту же /mock-interviews/:id, где результаты открываются в
        // обычном потоке.
        navigate(`/mock-interviews/${attempt._id}`);
    }

    let localQuizIteration;
    localQuizIteration = quizIteration;
    //console.log("qqqqq propspropspropsprops", props);
    useEffect(() => {
        loadQuiz()
    }, [questionId])

    useEffect(() => {
        if (!quizResults) {
            return;
        }
        saveResults(quizPerc)
    }, [quizResults])


    // VS-задачи группы входят в знаменатель наравне с квизами: решённая даёт балл.
    // js-задачи (js_<id>) сервер кладёт прямо в quizes — они уже в quizes.length.
    function getQuizPerc(quizHistory) {
        return calcQuizPerc(quizHistory.history, quizes.length + countVsQuizes(pubQuizes));
    }

    function saveResults(quizPerc, cb) {
        let total = (quizes || []).length + countVsQuizes(pubQuizes);
        onAction('complete_quiz', quizPerc)


        if (questionId) {
            let opts = {
                quizPerc,
                question: questionId || '',
                total,
                courseUserId: props.courseUserId,
                status: getIsQuizOk(quizPerc) ? 'ok' : 'error',
            }

            global.http.post('/save-course-module-question-results', opts)
                .then(r => {
                    setOpts(opts)
                    cb && cb();
                })
        } else {
            let opts = {
                quizPerc,
                total,
                courseModule: moduleId,
                courseUserId: props.courseUserId,
                status: getIsQuizOk(quizPerc) ? 'ok' : 'error',
            };
            global.http.post('/save-course-module-results', opts)
                .then(r => {
                    setOpts(opts)
                    cb && cb();
                })
        }
    }

    function loadQuiz(scb, ecb) {
        setLoading(new Date().getTime());
        setQuizResults(false);
        ++quizIteration
        let opts = {question: questionId || 0, _id: moduleId, cd: new Date().getTime()};
        setPubQuizes([])
        setLoadError(false)
        global.http.get('/load-quizes-by-module', opts).then((r) => {
            setLoading(false)
            //console.log("qqqqq LOADED INFORMATIONNNNNNNNN",);
            let items = [...r.quizes || []]
            setQuizes(items)
            setPubQuizes([...(r.pubQuizes || [])])
            scb && scb();
        }).catch(e => {
            setLoading(false)
            // пустой список - только по успешному ответу, иначе "Далее" засчитает главу без проверки
            setLoadError(true)
            window.notify.error(e.toString())
        })
    }

    function simpleGenerateQuiz(scb, ecb) {
        setLoading(false)
        if (!quizes.length) {
            setQuizPerc(100);
            setQuizResults(true);
            onClose();
        }
    }

    function reGenerateQuiz(scb, ecb) {
        setOpen(true)
        setOpts({});
        loadQuiz(scb, ecb);
        // global.http.get('/load-quizes-by-module', opts).then((r) => {
        //    //console.log("qqqqq rrrr4444444", r);
        //     let items = [...r.quizes];
        //     setQuizes(items)
        //     setPubQuizes(r.pubQuizes)
        //     scb && scb()
        //     setLoading(false)
        //     if (!items.length) {
        //         setQuizPerc(100);
        //         setQuizResults(true);
        //     }
        // })
    }

    function getIsQuizOk(quizPerc) {
        return quizPerc >= QUIZ_NORMAL
    }

    function onClose() {
        hideModal()
        opts && opts.status === 'ok' && onSuccess && onSuccess(opts)
    }

    function hideModal() {
        setOpen(false)
        setConfirmClose(false)
    }

    // Закрытие модалки посреди проверочного квиза (Esc, клик мимо) - только после подтверждения
    function beforeQuizClose() {
        if (quizResults || !_quizes.length) return true
        setConfirmClose(true)
        return false
    }


    let QUIZ_NORMAL = 85;
    let isQuizOk = getIsQuizOk(quizPerc);
    //console.log("qqqqq quizesquizesquizes ))", {pubQuizes, quizes, isLastModule, loading});
    let _quizes = pubQuizes

    //console.log("qqqqq pubQuizes ))", _quizes);
    let fn = (...args) => {
        //console.log("qqqqq ]]]]]]]]]]", args);
    }
    let isEmptyQuiz = !isLastModule && !_quizes.length && !loadError;

    useImperativeHandle(ref, () => ({
        // "Проверка знаний" не нужна, если для темы нет квиз-вопросов (isEmptyQuiz
        // сам себя закрывает без модалки) - готовность самих квизов проверяет
        // вызывающая сторона по qHistory, тут только доступность списка.
        hasPendingQuiz: () => !loading && !loadError && !isEmptyQuiz && !!_quizes.length,
        openQuiz: () => reGenerateQuiz(),
    }));

    return <div>
        {isModuleDone && <div data-testid={'course-module-passed'} style={{marginBottom: '16px'}}>
            <strong style={{display: 'block', marginBottom: '10px'}}>{isLastModule ? 'Курс пройден!' : 'Финальный тест сдан'}</strong>
            {moduleResult.quizPerc != null && <div style={{margin: '0 0 10px'}}>
                {t('yourPoints')}: {moduleResult.quizPerc}%
            </div>}
            {isLastModule && <Link to='/evaluations' className={'btn btn-sm btn-light'}>
                Смотреть ИИ оценку
            </Link>}
        </div>}
        {!!loading &&
            <button className={'btn btn-sm btn-primary'} style={{opacity: 1}} disabled={true}>
                {t('loadingResultsTesting')}
                ...</button>}
        {!loading && <>
            {(!!_quizes.length || hasInterview) && <Button className={'btn btn-sm btn-primary'}
                                                           id={questionId ? 'course-quiz-open-question-' + questionId : 'course-quiz-open-module-' + moduleId}
                                                           onClick={(scb) => {
                if (hasInterview) {
                    launchInterview(scb);
                    return;
                }
                reGenerateQuiz(scb)
            }}>
                <i className="iconoir-double-check"></i>
                {launchingInterview ? 'Открываем интервью...' : (title || t('checkKnowledge'))}</Button>}
            {loadError && <>
                <button id={'course-quiz-retry-' + (questionId || moduleId)} className={'btn btn-sm btn-primary'}
                        onClick={() => loadQuiz()}>
                    <i className="iconoir-refresh"></i> Повторить
                </button>
                <div><small>Не удалось загрузить задачи для проверки</small></div>
            </>}
            {isEmptyQuiz && <><Button
                // disabled={true}
                className={'btn btn-sm btn-primary'} onClick={(scb) => {
                saveResults(100, () => {
                })
                onSuccess && onSuccess({status: 'ok'}, () => {
                    scb && scb()
                })

            }}>
                <i className="iconoir-double-check"></i>
                {t('next')}</Button>
                <div>
                    <small>
                        {t('noCheckTasks')}
                    </small>
                </div>
            </>}
            {isLastModule && !_quizes.length && !hasInterview && !loadError &&
                <Link to='/courses' className={'btn btn-sm btn-primary'} onClick={(scb) => {
                    saveResults(100, () => {
                    })
                    onSuccess && onSuccess({status: 'ok'}, () => {
                        scb && scb()
                    })

                }}>{t('completeAndReturn')}</Link>}
        </>}


        <MyModal
            woClose={true}
            size={'xl'}
            isOpen={open}
            onClose={onClose}
            beforeClose={beforeQuizClose}
        >
            <>

                {!loading && !quizResults && !!_quizes.length && <div>
                    <TrainPageCourse
                        onResult={() => {
                            console.log("777777 qqqqq EEEEEEEEEEEEEEEEEEEEEEEEE onRESULT444",);
                            // setOpen(false)
                            setQuizResults(true)
                        }}
                        Result={ResultCourseQuizPage}
                        opts={{quizes: pubQuizes}}
                        onChange={(quizHistory, v) => {
                            let {data = {}, answerType, history} = quizHistory;

                            let time = data?.time || 1000;
                            // console.log("777777 qqqqq ON CHANGE EEEEEEEEEEEEEEEEEEEEEEEEE ]]", time, quizHistory, data, answerType);
                            let {quizPerc, total} = getQuizPerc(quizHistory)
                            //console.log("qqqqq quiz Perc ]]] ", quizPerc, total, v);
                            setQuizPerc(quizPerc);
                            // if (answerType === 'quiz' && !data?.data?.isCorrect) {
                            //     setTimeout(() =>{
                            //         setQuizResults(true)
                            //     }, time)
                            // }
                        }}
                    />
                </div>}

                {quizResults && <div className={'animChild'} data-testid={'course-quiz-result'}
                                     data-perc={quizPerc} data-ok={isQuizOk ? 'true' : 'false'}>
                    {/*{<div>*/}
                    <div className={'quiz-result-title'}>
                        {isQuizOk ? t('congratQuizMsg') : t('needMoreTimeQuizMsg')}
                    </div>
                    <hr/>
                    {!_quizes.length && <>
                        <strong style={{marginBottom: '10px', display: 'inline-block'}}>{t('goodMsg1')}</strong>
                        <div>
                            {t('goodMsg2')}


                        </div>
                    </>}
                    {!!_quizes.length && <div>
                        <strong style={{marginBottom: '10px', display: 'inline-block'}}>{t('quizResults')}</strong>
                        <div></div>
                        {t('yourPoints')}: {quizPerc}%
                        <div></div>
                        {t('needPoints')}: {QUIZ_NORMAL}%+
                    </div>}
                    <hr/>
                    {!isQuizOk && <>
                        <Button color={0} onClick={(scb) => {
                            setOpen(false)
                            scb && scb()
                        }}>{t('continueLearning')}</Button>
                        <Button color={4} onClick={(scb) => {
                            reGenerateQuiz(scb)
                        }}>
                            {t('restartQuiz')}
                        </Button>
                    </>

                    }

                    {isQuizOk && !isLastModule && <Button color={0} id={'course-quiz-finish'} onClick={(scb) => {
                        onClose()
                        scb && scb()
                    }
                    }>Отлично, перейти к след модулю</Button>}
                    {isQuizOk && isLastModule && <Button color={0} id={'course-quiz-finish'} onClick={(scb) => {
                        onClose()
                        scb && scb()
                    }
                    }>Отлично, ты прошел курс! Молодец!</Button>}
                    {isQuizOk && isLastModule && <div data-testid={'course-completed'} style={{marginTop: '16px'}}>
                        <strong>Курс пройден!</strong>
                        <div style={{margin: '6px 0 10px'}}>
                            ИИ оценка ваших ответов и решений доступна в разделе «Оценки».
                        </div>
                        <Link to='/evaluations' className={'btn btn-sm btn-light'} onClick={() => onClose()}>
                            Смотреть ИИ оценку
                        </Link>
                    </div>}


                </div>}
            </>


        </MyModal>

        <MyModal
            woClose={true}
            size={1}
            isOpen={confirmClose}
            onClose={() => setConfirmClose(false)}
        >
            <div data-testid={'course-quiz-confirm-close'}>
                <strong>Прервать проверку знаний?</strong>
                <div style={{margin: '10px 0 16px'}}>Прогресс этой попытки не сохранится.</div>
                <Button color={4} id={'course-quiz-confirm-stay'} onClick={(scb) => {
                    setConfirmClose(false)
                    scb && scb()
                }}>Продолжить квиз</Button>
                <Button color={0} id={'course-quiz-confirm-leave'} onClick={(scb) => {
                    hideModal()
                    scb && scb()
                }}>Закрыть и выйти</Button>
            </div>
        </MyModal>

        {interviewActive && <MockInterviewIframe
            interview={interviewActive}
            onClose={closeInterview}
            onComplete={completeInterview}
        />}

        {hasInterview && <CourseInterviewHistory interviewId={interviewId} reloadKey={historyReloadKey}/>}
        {/*{!!_quizes.length && <TrainPageCourse*/}
        {/*    onResult={() => {*/}
        {/*        setOpen(false)*/}
        {/*    }}*/}
        {/*    Result={ResultCourseQuizPage}*/}
        {/*    opts={{quizes: pubQuizes}}*/}
        {/*/>}*/}
    </div>
}

function ResultCourseQuizPage(props) {
    return <div>Result Page333!!!!!</div>
}

CourseQuiz = forwardRef(CourseQuiz);

export default CourseQuiz
